// Referee Trend — D3 SVG card-count bar chart (Phase 3 → M2 wave 7).
//
// One referee's recent matches, each a stacked bar of yellow + red card blocks
// with a hover overlay. React owns the container + <svg>; D3 draws inside a
// useEffect (a full clear+redraw, matching the legacy — no animation here, it
// rebuilds each render). ResizeObserver cleaned up in the effect return. Emits
// the same DOM as the legacy renderer (verified by the VRT). Data now comes from
// app-api via `useWidgetData` (was the `getRefTrendData` getter); the referee is
// selected client-side.

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import { selectRefTrend, type RefTrendMatch } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface RefereeTrendProps {
  /** Fixture — the endpoint is fixture-scoped (see RefereeProfile). */
  fixture: string;
  ref?: string | null;
}

// Path helper: a rounded rect with independent corner radii (legacy rRect).
function rRect(
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): string {
  const cap = Math.min(w / 2, h / 2);
  const rr = Math.min(r, cap);
  return (
    `M${x + rr},${y} H${x + w - rr} A${rr},${rr} 0 0 1 ${x + w},${y + rr}` +
    ` V${y + h - rr} A${rr},${rr} 0 0 1 ${x + w - rr},${y + h}` +
    ` H${x + rr} A${rr},${rr} 0 0 1 ${x},${y + h - rr}` +
    ` V${y + rr} A${rr},${rr} 0 0 1 ${x + rr},${y} Z`
  );
}

function draw(svgEl: SVGSVGElement, wrap: HTMLElement, matches: RefTrendMatch[]): void {
  const W = wrap.clientWidth;
  const H = wrap.clientHeight;
  if (W < 20 || H < 20) return;
  const M = 4;
  const cW = W - M * 2;
  const cH = H - M * 2;
  if (cW < 10 || cH < 10) return;

  const n = matches.length;
  const maxTotal = Math.max(1, ...matches.map((d) => d.yc + d.rc));
  const barW = Math.max(2, (cW * 0.75) / n);
  const gapW = n > 1 ? (cW - barW * n) / (n - 1) : 0;
  const barX = (i: number) => M + i * (barW + gapW);
  const pxPerU = cH / maxTotal;
  const base = M + cH;

  const svg = select(svgEl).attr("width", W).attr("height", H);
  svg.selectAll("*").remove();

  const R = Math.min(3, barW * 0.4);

  matches.forEach((m, i) => {
    const x = barX(i);
    const ycPx = m.yc * pxPerU;
    const gap = Math.max(1, pxPerU * 0.12);
    for (let k = 0; k < m.yc; k++) {
      const by = base - (k + 1) * pxPerU + gap / 2;
      svg.append("path").attr("d", rRect(x, by, barW, pxPerU - gap, R)).attr("fill", "#f1c40f");
    }
    for (let k = 0; k < m.rc; k++) {
      const by = base - ycPx - (k + 1) * pxPerU + gap / 2;
      svg.append("path").attr("d", rRect(x, by, barW, pxPerU - gap, R)).attr("fill", "#e74c3c");
    }
    if (m.yc === 0 && m.rc === 0) {
      svg
        .append("rect")
        .attr("x", x)
        .attr("width", barW)
        .attr("y", base - 2)
        .attr("height", 2)
        .attr("fill", "#333")
        .attr("rx", 0.5);
    }
    svg
      .append("rect")
      .attr("x", x)
      .attr("width", barW)
      .attr("y", M)
      .attr("height", cH)
      .attr("fill", "transparent")
      .attr(
        "data-tooltip",
        `${m.home} v ${m.away}  ${m.date}\nYellow: ${m.yc}  Red: ${m.rc}`
      );
  });
}

export default function RefereeTrend({ fixture, ref }: RefereeTrendProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const chosen = ref || "M. Oliver";
  const { data, isPending, isError, refetch } = useWidgetData(
    "referee-trend",
    useFixtureKey(fixture)
  );
  const matches = selectRefTrend(data ?? {}, chosen);

  useEffect(() => {
    const wrap = wrapRef.current;
    const svgEl = svgRef.current;
    if (!wrap || !svgEl || matches.length === 0) return;
    const render = () => draw(svgEl, wrap, matches);
    render();

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(render);
    ro.observe(wrap);
    return () => ro.disconnect();
    // Re-draw when the referee changes OR the fetch resolves — `data` (a stable
    // React Query ref) in the deps covers data-arrival; `chosen` the ref switch.
  }, [chosen, data]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  if (matches.length === 0) {
    return (
      <div className="data-wrapper top-align">
        <div className="module-empty">No data</div>
      </div>
    );
  }

  return (
    <div className="data-wrapper rft-wrapper" ref={wrapRef}>
      <svg className="rft-svg" ref={svgRef} />
    </div>
  );
}
